import { EdnaWrapper } from '@providers/index';
import { FC, useMemo, memo } from 'react';
import styled from 'styled-components';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { ProgressMeterProps } from './types';

interface IndicatorStyleProps {
  $progressPercentage: number;
  color?: string;
}

const ProgressWrapper = styled(ProgressPrimitive.Root)<ProgressMeterProps>`
  position: relative;
  overflow: hidden;
  background: ${(props) => props.theme.colors.grey100};
  border-radius: 2px;
  width: ${(props) => props.width};
  height: 4px;
`;

const Indicator = styled(ProgressPrimitive.Indicator)<IndicatorStyleProps>`
  background-color: ${(props) => props.color || props.theme.colors.primary};
  width: 100%;
  height: 100%;
  transform: translateX(-${(props) => 100 - props.$progressPercentage}%);
`;

const getProgressPercentage = (value: number, max: number): number => {
  if (value >= max) {
    return 100;
  }

  if (value <= 0) {
    return 0;
  }

  return (value / max) * 100;
};

const ProgressMeter: FC<ProgressMeterProps> = ({
  value,
  max,
  color,
  className,
  width = '180px',
  role = 'progressbar',
  ariaLabel,
}: ProgressMeterProps) => {
  const progressPercentage = useMemo(
    () => getProgressPercentage(value, max),
    [value, max]
  );

  return (
    <EdnaWrapper>
      <ProgressWrapper
        max={max}
        value={value}
        role={role}
        className={className}
        width={width}
        aria-label={ariaLabel}
      >
        <Indicator $progressPercentage={progressPercentage} color={color} />
      </ProgressWrapper>
    </EdnaWrapper>
  );
};

export default memo(ProgressMeter);
